import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import "./ERPAndCRMIntegrationPage.scss";
import heroBanner from "../../assets/images/banner-erp-integration.png";
import Copyright from "../../components/Copyright/Copyright";

const ERPAndCRMIntegrationPage = () => {
  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="feature-page">
      {/* Hero Section */}
      <section className="feature-hero">
        <div className="container">
          <div className="feature-hero-image">
            <img src={heroBanner} alt="ERP and CRM Integration" />
          </div>
        </div>
      </section>

      {/* ERP and CRM Integration Information */}
      <section className="feature-info">
        <div className="container">
          <div className="feature-grid">
            <div className="feature-details">
              <h2>ERP and CRM integration</h2>
              <p>
                CashManager provides seamless integration capabilities with your existing ERP and CRM systems,
                creating a unified platform for comprehensive business management and financial operations.
              </p>

              <div className="feature-methods three-columns">
                <div className="feature-method">
                  <div className="method-content">
                    <h3>API-based integration</h3>
                    <p>Robust RESTful APIs and webhooks for real-time data synchronization and seamless workflow automation.</p>
                  </div>
                </div>

                <div className="feature-method">
                  <div className="method-content">
                    <h3>Database connectivity</h3>
                    <p>Direct database connections and data mapping for efficient information exchange between systems.</p>
                  </div>
                </div>

                <div className="feature-method">
                  <div className="method-content">
                    <h3>File-based exchange</h3>
                    <p>Automated import/export capabilities supporting various file formats for batch processing and data migration.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="feature-features">
              <h2>Key features</h2>

              <div className="features-list">
                <div className="feature-item">
                  <h3>Real-time synchronization</h3>
                  <p>Keep your financial data synchronized across all systems with real-time updates and automated workflows.</p>
                </div>

                <div className="feature-item">
                  <h3>Custom data mapping</h3>
                  <p>Flexible data mapping and transformation to ensure compatibility between different system architectures.</p>
                </div>

                <div className="feature-item">
                  <h3>Error handling & monitoring</h3>
                  <p>Comprehensive error handling with detailed logging and monitoring for reliable data exchange.</p>
                </div>

                <div className="feature-item">
                  <h3>Security & compliance</h3>
                  <p>Enterprise-grade security with encryption, authentication, and compliance with data protection regulations.</p>
                </div>

                <div className="feature-item">
                  <h3>Scalable architecture</h3>
                  <p>Built to handle growing data volumes and increasing integration complexity as your business expands.</p>
                </div>

                <div className="feature-item">
                  <h3>Custom development</h3>
                  <p>Tailored integration solutions developed specifically for your unique business requirements and workflows.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Supported Systems Section */}
      <section className="supported-section">
        <div className="container">
          <div className="section-header">
            <h2>Supported systems & platforms</h2>
            <p>CashManager integrates with a wide range of ERP and CRM systems to ensure compatibility with your existing infrastructure. <Link to="/contact">Contact us</Link> to discuss your specific integration needs.</p>
          </div>

          <div className="items-grid">
            <div className="item">
              <h3>ERP systems</h3>
              <ul>
                <li>Epicor iScala</li>
                <li>Microsoft Dynamics 365 Business Central</li>
                <li>Microsoft Dynamics NAV</li>
                <li>Visma Horizon</li>
                <li>Other ERP systems - <Link to="/contact">Contact us</Link> for custom integration</li>
              </ul>
            </div>

            <div className="item">
              <h3>Integration methods</h3>
              <ul>
                <li>RESTful APIs</li>
                <li>SOAP web services</li>
                <li>Database connections</li>
                <li>File-based exchange</li>
                <li>Other methods - <Link to="/contact">Contact us</Link> for options</li>
              </ul>
            </div>

            <div className="item">
              <h3>Data formats</h3>
              <ul>
                <li>JSON</li>
                <li>XML</li>
                <li>CSV</li>
                <li>Excel</li>
                <li>Custom formats - <Link to="/contact">Contact us</Link> for support</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Integration Process */}
      <section className="implementation-process">
        <div className="container">
          <div className="section-header">
            <h2>Integration process</h2>
            <p>Get started with ERP and CRM integration in just a few simple steps</p>
          </div>

          <div className="process-steps">
            <div className="process-step">
              <div className="step-number">1</div>
              <div className="step-content">
                <h3>System analysis</h3>
                <p>We analyze your current systems, data structures, and integration requirements to design the optimal solution.</p>
              </div>
            </div>

            <div className="process-step">
              <div className="step-number">2</div>
              <div className="step-content">
                <h3>Integration design</h3>
                <p>Design custom integration architecture with data mapping, security protocols, and workflow automation.</p>
              </div>
            </div>

            <div className="process-step">
              <div className="step-number">3</div>
              <div className="step-content">
                <h3>Development & testing</h3>
                <p>Develop and thoroughly test the integration in a secure environment to ensure reliability and performance.</p>
              </div>
            </div>

            <div className="process-step">
              <div className="step-number">4</div>
              <div className="step-content">
                <h3>Deployment & support</h3>
                <p>Deploy to production with comprehensive training and ongoing support for your team.</p>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to integrate your systems?</h2>
            <p>Contact our team to discuss your integration requirements and create a unified business management solution.</p>
            <Link to="/onboarding" className="btn btn-secondary btn-large">Get started</Link>
          </div>
        </div>
      </section>

      {/* Copyright Section */}
      <Copyright />
    </div>
  );
};

export default ERPAndCRMIntegrationPage;
